// resources/js/adapters/vue-module.ts
import { createApp, createSSRApp, defineAsyncComponent, h, shallowRef } from 'vue';
import type { ComponentModule, MountedHandle } from '@/types/core';

function normalizeProps<P>(p: P): Record<string, any> {
    if (p && typeof p === 'object' && !Array.isArray(p)) return p as any;
    return {};
}

/**
 * Wrap a Vue SFC (loaded via dynamic import) into a ComponentModule.
 * - Uses defineAsyncComponent for code-splitting.
 * - Exposes `patch(nextProps)` via a shallowRef to enable live prop updates.
 * - `hydrate()` currently aliases `mount()`; wire up Vue SSR later if needed.
 */
export function vueModule<P = any>(importer: () => Promise<any>): ComponentModule<P> {
    return {
        mount(el: HTMLElement, initialProps: P): MountedHandle {
            const propsRef = shallowRef<Record<string, any>>(normalizeProps(initialProps));
            const Comp = defineAsyncComponent(async () => {
                const mod = await importer();
                return mod?.default ?? mod;
            });
            const app = createApp({ render: () => h(Comp, propsRef.value as any) });
            app.mount(el);
            return {
                patch(next: P) {
                    propsRef.value = normalizeProps(next);
                },
                dispose() {
                    app.unmount();
                },
            };
        },
        async hydrate(el: HTMLElement, props: P): Promise<MountedHandle> {
            const propsRef = shallowRef(props);
            // *** resolve importer BEFORE mounting ***
            const mod = await importer();
            const Comp = mod?.default ?? mod;

            const app = createSSRApp({ render: () => h(Comp, propsRef.value as any) });
            app.mount(el); // Vue will hydrate the existing SSR HTML
            return {
                patch(next) {
                    propsRef.value = next;
                },
                dispose() {
                    app.unmount();
                },
            };
        },
    };
}
